import { useState } from "react";
import { ArrowUpRight, Globe, Instagram, Mail, MoveRight, Tag, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  CATEGORIES,
  buildMailto,
  normalizeUrl,
  instagramUrl,
} from "@/data/suppliers";

const HERO_IMAGE = "/hero.png";

const CategoryTile = ({ category, index, onOpen }) => (
  <button
    type="button"
    data-testid={`category-tile-${index}`}
    onClick={() => onOpen(category)}
    className="group relative aspect-[7/4] overflow-hidden rounded-2xl bg-stone-900 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-stone-900 focus-visible:ring-offset-4 focus-visible:ring-offset-[#F9F8F6]"
  >
    <img
      src={category.image}
      alt={category.name}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/25 to-transparent transition-opacity duration-500 group-hover:from-stone-950/90" />
    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-6 md:p-8">
      <span className="text-[10px] uppercase tracking-[0.28em] text-white/60">
        {String(index + 1).padStart(2, "0")} — {category.suppliers.length} suppliers
      </span>
      <h3 className="font-serif text-2xl font-light leading-tight text-white md:text-3xl">
        {category.name}
      </h3>
      <span className="mt-1 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-white/70 opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0">
        Explore <MoveRight className="h-3.5 w-3.5" />
      </span>
    </div>
  </button>
);

const SupplierRow = ({ supplier, index }) => {
  const web = normalizeUrl(supplier.website);
  const insta = instagramUrl(supplier.socials);
  return (
    <div
      data-testid={`supplier-row-${index}`}
      className="border-b border-stone-200 py-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-serif text-xl font-normal text-stone-900">
            {supplier.name}
          </h4>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            {web && (
              <a
                data-testid={`supplier-website-${index}`}
                href={web}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-stone-500 transition-colors hover:text-stone-900"
              >
                <Globe className="h-3.5 w-3.5" /> Website
              </a>
            )}
            {insta && (
              <a
                data-testid={`supplier-instagram-${index}`}
                href={insta}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-stone-500 transition-colors hover:text-stone-900"
              >
                <Instagram className="h-3.5 w-3.5" /> Instagram
              </a>
            )}
          </div>
        </div>
      </div>

      {supplier.discount && (
        <div
          data-testid={`supplier-discount-${index}`}
          className="mt-4 flex items-center gap-3 border border-[#A88F5C]/40 bg-[#A88F5C]/10 px-4 py-3"
        >
          <Tag className="h-4 w-4 shrink-0 text-[#A88F5C]" />
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-serif text-lg font-medium text-[#8f7645]">
              {supplier.discount.amount}
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500">
              Code:
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-900">
              {supplier.discount.code}
            </span>
          </div>
        </div>
      )}

      {supplier.email ? (
        <a
          data-testid={`supplier-enquire-${index}`}
          href={buildMailto(supplier.email)}
          className="mt-4 inline-flex items-center gap-2 bg-stone-900 px-6 py-3 text-[11px] uppercase tracking-[0.22em] text-[#F9F8F6] transition-colors duration-300 hover:bg-[#6B705C]"
        >
          <Mail className="h-3.5 w-3.5" /> Enquire
        </a>
      ) : web || insta ? (
        <a
          data-testid={`supplier-visit-${index}`}
          href={web || insta}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 border border-stone-900 px-6 py-3 text-[11px] uppercase tracking-[0.22em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-[#F9F8F6]"
        >
          Visit <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      ) : (
        <p
          data-testid={`supplier-note-${index}`}
          className="mt-4 text-[11px] uppercase tracking-[0.16em] text-stone-400"
        >
          Contact via the Gledswood events team
        </p>
      )}
    </div>
  );
};

export default function Directory() {
  const [active, setActive] = useState(null);
  const open = Boolean(active);

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-stone-900">
      {/* Header */}
      <header
        data-testid="site-header"
        className="fixed top-0 z-40 w-full border-b border-stone-200/60 bg-[#F9F8F6]/85 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <img
            src="/logo-header.png"
            alt="Gledswood Homestead & Winery"
            data-testid="site-logo"
            className="h-10 w-auto md:h-12"
          />
          <span className="hidden text-[10px] uppercase tracking-[0.28em] text-[#A88F5C] md:block">
            Preferred Wedding Suppliers
          </span>
        </div>
      </header>

      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative flex min-h-[88vh] items-end overflow-hidden"
      >
        <img
          src={HERO_IMAGE}
          alt="Gledswood Homestead & Winery"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-950/45" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 md:px-10 md:pb-28">
          <img
            src="/logo-gold.png"
            alt="Gledswood Homestead & Winery"
            data-testid="hero-logo"
            className="mb-8 h-16 w-auto md:h-20"
          />
          <h1 className="max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-white md:text-7xl lg:text-8xl">
            Preferred Wedding Suppliers
          </h1>
          <p className="mt-8 max-w-xl text-sm font-light leading-relaxed tracking-wide text-white/80 md:text-base">
            Our collection of the trusted vendors the Gledswood Events Team
            recommends to bring your event to life. Click on your preferred
            vendor and message them for more details about their services.
          </p>
          <a
            href="#directory"
            data-testid="hero-cta"
            className="mt-10 inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-[11px] uppercase tracking-[0.24em] text-white transition-colors duration-300 hover:bg-white hover:text-stone-900"
          >
            Browse the directory <MoveRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* Directory */}
      <section
        id="directory"
        data-testid="directory-section"
        className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32"
      >
        <div className="mb-14 max-w-2xl">
          <span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">
            The Directory
          </span>
          <h2 className="mt-4 font-serif text-4xl font-light tracking-tight text-stone-900 md:text-5xl">
            Explore by category
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {CATEGORIES.map((category, index) => (
            <CategoryTile
              key={category.name}
              category={category}
              index={index}
              onOpen={setActive}
            />
          ))}

          {/* Closing tile to complete the 3 x 5 grid */}
          <div
            data-testid="closing-tile"
            className="relative flex aspect-[7/4] flex-col justify-between overflow-hidden rounded-2xl bg-stone-900 p-6 text-[#F9F8F6] md:p-8"
          >
            <img
              src="/categories/closing-heritage.jpg"
              alt="Gledswood Homestead & Winery"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/50 to-stone-950/30" />
            <span className="relative z-10 text-[10px] uppercase tracking-[0.28em] text-white/60">
              15 — Gledswood
            </span>
            <div className="relative z-10">
              <p className="font-serif text-2xl font-light leading-snug md:text-3xl">
                Where heritage meets the beginning of forever.
              </p>
              <a
                href="https://www.gledswood.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-white/80 transition-colors hover:text-white"
              >
                Visit Gledswood <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        data-testid="site-footer"
        className="border-t border-stone-200 bg-[#F9F8F6]"
      >
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <p
            data-testid="footer-referral-text"
            className="mx-auto max-w-3xl text-center font-serif text-2xl font-light leading-snug text-stone-800 md:text-3xl"
          >
            We love working with professional and talented vendors, so if you
            know of someone we need to know about, please put us in touch!
          </p>
          <div className="mt-12 flex flex-col items-center gap-4 border-t border-stone-200 pt-10 md:flex-row md:items-center md:justify-between">
            <img
              src="/logo.png"
              alt="Gledswood Homestead & Winery"
              className="h-10 w-auto mix-blend-multiply"
            />
            <p className="text-xs font-light tracking-wide text-stone-500">
              © {new Date().getFullYear()} Gledswood Homestead & Winery. Curated with care.
            </p>
          </div>
        </div>
      </footer>

      {/* Supplier drawer */}
      <Sheet open={open} onOpenChange={(v) => !v && setActive(null)}>
        <SheetContent
          data-testid="supplier-sheet"
          side="right"
          className="w-full overflow-y-auto border-l-0 bg-[#F9F8F6] p-0 sm:max-w-md"
        >
          {active && (
            <div>
              <SheetTitle className="sr-only">{active.name} suppliers</SheetTitle>
              <SheetDescription className="sr-only">
                Preferred {active.name} suppliers for Gledswood Homestead & Winery. Select Enquire to email a supplier.
              </SheetDescription>
              <div className="relative h-52 w-full overflow-hidden">
                <img
                  src={active.image}
                  alt={active.name}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 to-transparent" />
                <SheetClose
                  data-testid="sheet-close"
                  className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center bg-[#F9F8F6]/90 text-stone-900 transition-colors hover:bg-white"
                >
                  <X className="h-4 w-4" />
                </SheetClose>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-white/70">
                    {active.suppliers.length} preferred suppliers
                  </span>
                  <h3 className="mt-1 font-serif text-3xl font-light text-white">
                    {active.name}
                  </h3>
                </div>
              </div>

              <div className="px-6 pb-10">
                {active.suppliers.map((supplier, i) => (
                  <SupplierRow key={supplier.name + i} supplier={supplier} index={i} />
                ))}
                <p className="mt-6 text-xs font-light leading-relaxed tracking-wide text-stone-500">
                  Selecting “Enquire” opens a pre-written introduction in your
                  email app, ready to send to the supplier.
                </p>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
